import { usePermissionCheckedAction } from "@/hooks/usePermissionCheckedAction"
import type { DeviceIdProps } from "@/redux/models"
import { getDeviceSummaryByDeviceIdFromState } from "@/redux/selectors"
import { useRootSelector } from "@/redux/useRootSelector"
import type { BoxProps } from "@/theme"
import {
  AlertBodyText,
  AlertCard,
  Box,
  Button,
  COLORS,
  CloseIconButton,
} from "@/theme"
import i18n from "@/translations/i18n"
import { useRouter } from "expo-router"
import React from "react"
import { useTranslation } from "react-i18next"

interface Props extends DeviceIdProps, BoxProps {
  isDismissable: boolean
}

/**
 * Warning to display when a device is unconfigured
 */
export function UnconfiguredDeviceWarning({
  deviceId,
  isDismissable,

  ...rest
}: Props): React.JSX.Element | null {
  const [isOpen, setIsOpen] = React.useState(true)
  const { t } = useTranslation("deviceConfiguration")
  const withPermissions = usePermissionCheckedAction()
  const router = useRouter()

  const device = useRootSelector((state) =>
    getDeviceSummaryByDeviceIdFromState(state, deviceId),
  )
  const isUnconfigured = device?.deviceInstallationType === "unconfigured"

  if (isUnconfigured && isOpen) {
    return (
      <AlertCard
        {...rest}
        severity={"warning"}
        titleText={i18n.t("warning")}
        actionElement={
          isDismissable ? (
            <Box ml={"$2"}>
              <CloseIconButton
                iconColor={COLORS.$textDark.primary}
                variant={"text"}
                onPress={() => setIsOpen(false)}
              />
            </Box>
          ) : null
        }
      >
        <Box mb={"$2"}>
          <AlertBodyText>{t("unconfiguredDeviceWarning")}</AlertBodyText>
        </Box>
        <Box maxW={"$xs"} ml={"auto"} w={"$full"}>
          <Button
            id={"createConfigurationLink"}
            text={t("setUpDevice")}
            onPress={withPermissions({
              callback: () =>
                router.navigate({
                  params: { codaDeviceAlias: device.codaDeviceAlias },
                  pathname: "/devices/[codaDeviceAlias]/setup",
                }),
              required: "canManageDeviceConfiguration",
            })}
          />
        </Box>
      </AlertCard>
    )
  }
  return null
}
